type FetchArgs = Parameters<typeof fetch>;

interface ResponseSnapshot {
  body: ArrayBuffer;
  headers: Headers;
  status: number;
  statusText: string;
}

export interface AuthTokenFetchGuardOptions {
  fetch: typeof fetch;
  isTokenRequest: (url: string) => boolean;
  isSuppressed: () => boolean;
  onInvalidToken: () => void;
  onRateLimited: () => void;
}

export interface AuthTokenFetchGuard {
  fetch: typeof fetch;
  resetInvalidTokenCircuit: () => void;
}

function requestUrl(input: FetchArgs[0]): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

async function snapshot(response: Response): Promise<ResponseSnapshot> {
  return {
    body: await response.arrayBuffer(),
    headers: new Headers(response.headers),
    status: response.status,
    statusText: response.statusText,
  };
}

function restoreResponse(response: ResponseSnapshot): Response {
  return new Response(response.body.byteLength > 0 ? response.body.slice(0) : null, {
    headers: new Headers(response.headers),
    status: response.status,
    statusText: response.statusText,
  });
}

function jsonResponse(status: number, body: Record<string, string>): ResponseSnapshot {
  const encoded = new TextEncoder().encode(JSON.stringify(body));
  return {
    body: encoded.buffer,
    headers: new Headers({ "Content-Type": "application/json" }),
    status,
    statusText: status === 400 ? "Bad Request" : status === 429 ? "Too Many Requests" : "Service Unavailable",
  };
}

function temporaryAuthFailure(): ResponseSnapshot {
  return jsonResponse(503, {
    code: "request_timeout",
    message: "Authentication is temporarily unavailable.",
  });
}

/**
 * Supabase serialises refreshes inside one GoTrue client, but a cold Next.js boot can
 * temporarily create several callers before the client singleton and persisted mobile
 * storage have settled. If the stored refresh token is stale, every queued caller can
 * otherwise send the same doomed request before the first 400 clears storage.
 *
 * This guard is deliberately below every auth/session helper. It makes the token endpoint
 * single-flight even if callers or client instances overlap, and opens a terminal circuit
 * after the first invalid-token response. A successful explicit sign-in resets the circuit.
 */
export function createAuthTokenFetchGuard(options: AuthTokenFetchGuardOptions): AuthTokenFetchGuard {
  let inFlight: Promise<ResponseSnapshot> | null = null;
  let invalidTokenCircuitOpen = false;

  const guardedFetch = async (...args: FetchArgs): Promise<Response> => {
    const url = requestUrl(args[0]);
    if (!options.isTokenRequest(url)) {
      return options.fetch(...args);
    }

    if (invalidTokenCircuitOpen) {
      /* Only the request that discovered the invalid refresh token may receive the 400.
       * GoTrue treats every 400 as a terminal session failure and removes persisted
       * storage. Returning that same terminal response to queued callers made all of
       * them issue native Preferences.remove calls at once on iOS, overwhelming the
       * Capacitor bridge even though only one request reached the network. */
      return restoreResponse(temporaryAuthFailure());
    }

    if (options.isSuppressed()) {
      return restoreResponse(
        jsonResponse(429, {
          code: "over_request_rate_limit",
          message: "Token refresh is temporarily paused.",
        }),
      );
    }

    const isFollower = inFlight !== null;
    if (!inFlight) {
      const currentRequest = (async () => {
        let response: ResponseSnapshot;
        try {
          response = await snapshot(await options.fetch(...args));
        } catch {
          return temporaryAuthFailure();
        }

        if (response.status === 400) {
          invalidTokenCircuitOpen = true;
          options.onInvalidToken();
        } else if (response.status === 429) {
          options.onRateLimited();
        }

        return response;
      })();

      inFlight = currentRequest;
      void currentRequest.finally(() => {
        if (inFlight === currentRequest) inFlight = null;
      });
    }

    const response = await inFlight;
    if (isFollower && response.status === 400) {
      return restoreResponse(temporaryAuthFailure());
    }
    return restoreResponse(response);
  };

  return {
    fetch: guardedFetch as typeof fetch,
    resetInvalidTokenCircuit: () => {
      invalidTokenCircuitOpen = false;
    },
  };
}
